import { useState, useEffect, useCallback } from 'react';
import { useApi, useToaster } from '../../../utils/hooks';
import { ToastStyle } from '../../../utils/hooks/useToaster';
import {
  IManageInvoicesMetrics,
  IClubMemberInvoiceExportDto
} from '../../../utils/constants/interfaces';
import { Helpers } from '../../../utils/helpers';

// Define the return type interface
export interface UseManageInvoicesReturn {
  metrics: IManageInvoicesMetrics | null;
  loading: boolean;
  error: string | null;
  refreshMetrics: () => void;
  showGenerateAnnualInvoicesModal: boolean;
  openGenerateAnnualInvoicesModal: () => void;
  closeGenerateAnnualInvoicesModal: () => void;
  generateAnnualInvoices: () => Promise<void>;
  showGenerateNewCandidateInvoicesModal: boolean;
  openGenerateNewCandidateInvoicesModal: () => void;
  closeGenerateNewCandidateInvoicesModal: () => void;
  generateNewCandidateInvoices: () => Promise<void>;
  showGenerateMissingInvoicesModal: boolean;
  openGenerateMissingInvoicesModal: () => void;
  closeGenerateMissingInvoicesModal: () => void;
  generateMissingInvoices: () => Promise<void>;
  showApplyLateFeesModal: boolean;
  openApplyLateFeesModal: () => void;
  closeApplyLateFeesModal: () => void;
  applyLateFees: () => Promise<void>;
  exportNewCandidateInvoices: () => Promise<void>;
  exportMissingInvoices: () => Promise<void>;
  isGenerating: boolean;
  generatingMessage: string;
  ToastContainer: React.FC;
}

export const useManageInvoices = (): UseManageInvoicesReturn => {
  const api = useApi();
  const toaster = useToaster();

  const [metrics, setMetrics] = useState<IManageInvoicesMetrics | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showGenerateAnnualInvoicesModal, setShowGenerateAnnualInvoicesModal] =
    useState<boolean>(false);
  const [showGenerateNewCandidateInvoicesModal, setShowGenerateNewCandidateInvoicesModal] =
    useState<boolean>(false);
  const [showGenerateMissingInvoicesModal, setShowGenerateMissingInvoicesModal] =
    useState<boolean>(false);
  const [showApplyLateFeesModal, setShowApplyLateFeesModal] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatingMessage, setGeneratingMessage] = useState<string>('');

  const fetchMetrics = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = '/billing/manage-invoices/invoice-metrics';
      const apiResponse = await api.get<IManageInvoicesMetrics>(endpoint);

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
        setMetrics(apiResponse.response);
      } else {
        const errorMessage = 'Failed to fetch invoice metrics';
        setError(errorMessage);
        toaster.addToast(errorMessage, ToastStyle.ERROR, 3000);
      }
    } catch (error) {
      const errorMessage = 'Error fetching invoice metrics';
      setError(errorMessage);
      toaster.addToast(errorMessage, ToastStyle.ERROR, 3000);
    } finally {
      setLoading(false);
    }
  }, [api, toaster]);

  // Modal handlers
  const openGenerateAnnualInvoicesModal = useCallback(() => {
    setShowGenerateAnnualInvoicesModal(true);
  }, []);

  const closeGenerateAnnualInvoicesModal = useCallback(() => {
    setShowGenerateAnnualInvoicesModal(false);
  }, []);

  const openGenerateNewCandidateInvoicesModal = useCallback(() => {
    setShowGenerateNewCandidateInvoicesModal(true);
  }, []);

  const closeGenerateNewCandidateInvoicesModal = useCallback(() => {
    setShowGenerateNewCandidateInvoicesModal(false);
  }, []);

  const openGenerateMissingInvoicesModal = useCallback(() => {
    setShowGenerateMissingInvoicesModal(true);
  }, []);

  const closeGenerateMissingInvoicesModal = useCallback(() => {
    setShowGenerateMissingInvoicesModal(false);
  }, []);

  const openApplyLateFeesModal = useCallback(() => {
    setShowApplyLateFeesModal(true);
  }, []);

  const closeApplyLateFeesModal = useCallback(() => {
    setShowApplyLateFeesModal(false);
  }, []);

  // Generate Annual Invoices
  const generateAnnualInvoices = useCallback(async (): Promise<void> => {
    setIsGenerating(true);
    setGeneratingMessage('Generating annual invoices... This may take a moment.');
    closeGenerateAnnualInvoicesModal();

    try {
      const endpoint = '/billing/manage-invoices/generate-invoices';
      const requestBody = JSON.stringify({
        billingYear: metrics?.billingYear ?? new Date().getFullYear()
      });
      const apiResponse = await api.post<boolean>(endpoint, requestBody);

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
        toaster.addToast('Annual invoices generated successfully!', ToastStyle.SUCCESS, 5000);
        // Refresh metrics after successful generation
        await fetchMetrics();
      } else {
        toaster.addToast('Failed to generate annual invoices', ToastStyle.ERROR, 5000);
      }
    } catch (error) {
      toaster.addToast('Error generating annual invoices', ToastStyle.ERROR, 5000);
    } finally {
      setIsGenerating(false);
      setGeneratingMessage('');
    }
  }, [api, toaster, metrics?.billingYear, closeGenerateAnnualInvoicesModal, fetchMetrics]);

  // Generate New Candidate Invoices
  const generateNewCandidateInvoices = useCallback(async (): Promise<void> => {
    setIsGenerating(true);
    setGeneratingMessage('Generating new candidate invoices...');
    closeGenerateNewCandidateInvoicesModal();

    try {
      const endpoint = '/billing/manage-invoices/generate-new-candidate-invoices';
      const requestBody = JSON.stringify({
        billingYear: metrics?.billingYear ?? new Date().getFullYear()
      });
      const apiResponse = await api.post<boolean>(endpoint, requestBody);

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
        toaster.addToast(
          'New candidate invoices generated successfully!',
          ToastStyle.SUCCESS,
          5000
        );
        // Refresh metrics after successful generation
        await fetchMetrics();
      } else {
        toaster.addToast('Failed to generate new candidate invoices', ToastStyle.ERROR, 5000);
      }
    } catch (error) {
      toaster.addToast('Error generating new candidate invoices', ToastStyle.ERROR, 5000);
    } finally {
      setIsGenerating(false);
      setGeneratingMessage('');
    }
  }, [api, toaster, metrics?.billingYear, closeGenerateNewCandidateInvoicesModal, fetchMetrics]);

  // Generate Missing Invoices
  const generateMissingInvoices = useCallback(async (): Promise<void> => {
    setIsGenerating(true);
    setGeneratingMessage('Generating missing invoices...');
    closeGenerateMissingInvoicesModal();

    try {
      const endpoint = '/billing/manage-invoices/generate-missing-invoices';
      const requestBody = JSON.stringify({
        billingYear: metrics?.billingYear ?? new Date().getFullYear()
      });
      const apiResponse = await api.post<boolean>(endpoint, requestBody);

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
        toaster.addToast('Missing invoices generated successfully!', ToastStyle.SUCCESS, 5000);
        // Refresh metrics after successful generation
        await fetchMetrics();
      } else {
        toaster.addToast('Failed to generate missing invoices', ToastStyle.ERROR, 5000);
      }
    } catch (error) {
      toaster.addToast('Error generating missing invoices', ToastStyle.ERROR, 5000);
    } finally {
      setIsGenerating(false);
      setGeneratingMessage('');
    }
  }, [api, toaster, metrics?.billingYear, closeGenerateMissingInvoicesModal, fetchMetrics]);

  // Apply Late Fees
  const applyLateFees = useCallback(async (): Promise<void> => {
    setIsGenerating(true);
    setGeneratingMessage('Applying late fees to outstanding invoices...');
    closeApplyLateFeesModal();

    try {
      const endpoint = '/billing/manage-invoices/apply-late-fees';
      const requestBody = JSON.stringify({
        billingYear: metrics?.billingYear ?? new Date().getFullYear()
      });
      const apiResponse = await api.post<boolean>(endpoint, requestBody);

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
        toaster.addToast('Late fees applied successfully!', ToastStyle.SUCCESS, 5000);
        // Refresh metrics after successful application
        await fetchMetrics();
      } else {
        toaster.addToast('Failed to apply late fees', ToastStyle.ERROR, 5000);
      }
    } catch (error) {
      toaster.addToast('Error applying late fees', ToastStyle.ERROR, 5000);
    } finally {
      setIsGenerating(false);
      setGeneratingMessage('');
    }
  }, [api, toaster, metrics?.billingYear, closeApplyLateFeesModal, fetchMetrics]);

  // Export New Candidate Invoices
  const exportNewCandidateInvoices = useCallback(async (): Promise<void> => {
    setIsGenerating(true);
    setGeneratingMessage('Exporting new candidate invoice list...');

    try {
      const endpoint = '/billing/manage-invoices/export-new-candidate-invoices';
      const requestBody = JSON.stringify({
        billingYear: metrics?.billingYear ?? new Date().getFullYear()
      });
      const apiResponse = await api.post<IClubMemberInvoiceExportDto[]>(endpoint, requestBody);

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
        const filename = `new-candidate-invoices-${metrics?.billingYear || new Date().getFullYear()}.csv`;

        // Define column mapping for better CSV headers
        const columnMap = {
          clubMemberId: 'Club Member ID',
          firstName: 'First Name',
          lastName: 'Last Name',
          middleName: 'Middle Name',
          title: 'Title',
          suffix: 'Suffix',
          memberTypeName: 'Member Type',
          status: 'Status',
          email: 'Email',
          address: 'Address',
          address2: 'Address 2',
          city: 'City',
          state: 'State',
          zip: 'ZIP Code',
          phone: 'Phone',
          cellPhone: 'Cell Phone',
          dateOfBirthUtc: 'Date of Birth',
          firstJoinedDateUtc: 'First Joined Date',
          joinedDateUtc: 'Joined Date',
          profession: 'Profession',
          isNraMember: 'NRA Member',
          nraNumber: 'NRA Number',
          regularMemberDateUtc: 'Regular Member Date',
          honoraryMemberDateUtc: 'Honorary Member Date',
          seniorMemberDateUtc: 'Senior Member Date',
          lifeMemberDateUtc: 'Life Member Date',
          createdBy: 'Created By',
          createdDateUtc: 'Created Date',
          updatedBy: 'Updated By',
          updatedDateUtc: 'Updated Date',
          notes: 'Notes',
          emailConfirmed: 'Email Confirmed'
        };

        Helpers.exportToCSV(apiResponse.response, filename, columnMap);
        toaster.addToast('New candidate invoices exported successfully!', ToastStyle.SUCCESS, 3000);
      } else {
        toaster.addToast('Failed to export new candidate invoices', ToastStyle.ERROR, 5000);
      }
    } catch (error) {
      toaster.addToast('Error exporting new candidate invoices', ToastStyle.ERROR, 5000);
    } finally {
      setIsGenerating(false);
      setGeneratingMessage('');
    }
  }, [api, toaster, metrics?.billingYear]);

  // Export Missing Invoices
  const exportMissingInvoices = useCallback(async (): Promise<void> => {
    setIsGenerating(true);
    setGeneratingMessage('Exporting missing invoice list...');

    try {
      const endpoint = '/billing/manage-invoices/export-missing-invoices';
      const requestBody = JSON.stringify({
        billingYear: metrics?.billingYear ?? new Date().getFullYear()
      });
      const apiResponse = await api.post<IClubMemberInvoiceExportDto[]>(endpoint, requestBody);

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
        const filename = `missing-invoices-${metrics?.billingYear || new Date().getFullYear()}.csv`;

        // Define column mapping for better CSV headers
        const columnMap = {
          clubMemberId: 'Club Member ID',
          firstName: 'First Name',
          lastName: 'Last Name',
          middleName: 'Middle Name',
          title: 'Title',
          suffix: 'Suffix',
          memberTypeName: 'Member Type',
          status: 'Status',
          email: 'Email',
          address: 'Address',
          address2: 'Address 2',
          city: 'City',
          state: 'State',
          zip: 'ZIP Code',
          phone: 'Phone',
          cellPhone: 'Cell Phone',
          dateOfBirthUtc: 'Date of Birth',
          firstJoinedDateUtc: 'First Joined Date',
          joinedDateUtc: 'Joined Date',
          profession: 'Profession',
          isNraMember: 'NRA Member',
          nraNumber: 'NRA Number',
          regularMemberDateUtc: 'Regular Member Date',
          honoraryMemberDateUtc: 'Honorary Member Date',
          seniorMemberDateUtc: 'Senior Member Date',
          lifeMemberDateUtc: 'Life Member Date',
          createdBy: 'Created By',
          createdDateUtc: 'Created Date',
          updatedBy: 'Updated By',
          updatedDateUtc: 'Updated Date',
          notes: 'Notes',
          emailConfirmed: 'Email Confirmed'
        };

        Helpers.exportToCSV(apiResponse.response, filename, columnMap);
        toaster.addToast('Missing invoices exported successfully!', ToastStyle.SUCCESS, 3000);
      } else {
        toaster.addToast('Failed to export missing invoices', ToastStyle.ERROR, 5000);
      }
    } catch (error) {
      toaster.addToast('Error exporting missing invoices', ToastStyle.ERROR, 5000);
    } finally {
      setIsGenerating(false);
      setGeneratingMessage('');
    }
  }, [api, toaster, metrics?.billingYear]);

  // Fetch metrics on mount
  useEffect(() => {
    void fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh function to refetch metrics (useful after actions)
  const refreshMetrics = useCallback((): void => {
    void fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics,
    loading,
    error,
    refreshMetrics,
    showGenerateAnnualInvoicesModal,
    openGenerateAnnualInvoicesModal,
    closeGenerateAnnualInvoicesModal,
    generateAnnualInvoices,
    showGenerateNewCandidateInvoicesModal,
    openGenerateNewCandidateInvoicesModal,
    closeGenerateNewCandidateInvoicesModal,
    generateNewCandidateInvoices,
    showGenerateMissingInvoicesModal,
    openGenerateMissingInvoicesModal,
    closeGenerateMissingInvoicesModal,
    generateMissingInvoices,
    showApplyLateFeesModal,
    openApplyLateFeesModal,
    closeApplyLateFeesModal,
    applyLateFees,
    exportNewCandidateInvoices,
    exportMissingInvoices,
    isGenerating,
    generatingMessage,
    ToastContainer: toaster.ToastContainer
  };
};
